/**
 * formatHoursDecimal — single source of truth for displaying a decimal
 * hour count (e.g. 3.9999, 4.0, 5.5) as a French-style "Xh" / "XhMM"
 * string.
 *
 * Bug history (Feb 2026):
 *   The previous inline implementations did
 *       const h = Math.floor(decimal);
 *       const m = Math.round((decimal - h) * 60);
 *   which produced "3h60" for decimal=3.9999 because Math.round
 *   rounds the minutes to 60 BEFORE the hour carry-over is applied.
 *   That bug was triplicated in DashboardPage, TimeTrackingPage and
 *   UnassignedActivitiesTab.
 *
 * Fix:
 *   Compute total minutes FIRST, then derive (h, m) by integer
 *   division. This makes m ∈ [0, 59] mathematically guaranteed — no
 *   carry-over branch needed, no possible "h60" leak.
 *
 *   Examples
 *     3.9999 → 240 min → "4h"      (was "3h60")
 *     3.99   → 239 min → "3h59"
 *     4.0166 → 241 min → "4h01"
 *     5.5    → 330 min → "5h30"
 *     0      →   0 min → "-"
 */
export function formatHoursDecimal(decimal) {
  if (decimal === null || decimal === undefined || Number.isNaN(decimal)) return '-';
  if (decimal <= 0) return '-';
  const totalMin = Math.round(decimal * 60);
  if (totalMin <= 0) return '-';
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h}h` : `${h}h${m.toString().padStart(2, '0')}`;
}

export default formatHoursDecimal;
